import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import type { Stripe, StripeElementsOptionsMode } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import styled from 'styled-components'
import { rem } from 'polished'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextButton from 'LuxKit/Button/TextButton'
import RadioButton from 'LuxKit/Radio/RadioButton'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useAppSelector } from 'hooks/reduxHooks'
import { useStripeElementsLocale } from 'hooks/useStripeElementsLocale'
import { STRIPE_ELEMENTS_APPEARANCE } from 'lib/payment/stripeUtils'
import getStripeInstance, { type StripeInstanceResult } from 'lib/payment/getStripeInstance'
import { capitalise } from 'lib/string/stringUtils'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import {
  getSavedStripeCards,
  getStripeOffSessionCardsByRegionAndPurposeId,
  promoteStripeOffSessionCardToPrimaryByRegionAndPurposeId,
  type LuxPlusOffSessionPaymentCard,
} from 'api/payment'
import {
  appendLuxPlusBackupCard,
  hasMatchingLuxPlusCheckoutCardBackupCandidate,
  luxPlusOffSessionCardToLuxPlusCheckoutCardBackupCandidate,
  stripePaymentCardToLuxPlusCheckoutCardBackupCandidate,
  type LuxPlusCheckoutCardBackupCandidate,
} from 'checkout/lib/luxPlusCheckoutCardBackup'
import getLuxPlusCardExpiresBeforeRenewal from 'luxPlus/lib/getLuxPlusCardExpiresBeforeRenewal'
import LuxPlusAddBackupCardElement from './LuxPlusAddBackupCardElement'
import LuxPlusNewPrimaryCardElement from './LuxPlusNewPrimaryCardElement'
import updateLuxPlusPrimaryCard from './updateLuxPlusPrimaryCard'

type PickerMode = 'add-backup' | 'update-primary'

const CardOptionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

const CardOption = styled.label<{ $disabled?: boolean, $hasWarning?: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${rem(12)};
  align-items: center;
  min-height: ${rem(48)};
  padding: ${rem(12)};
  border: 1px solid ${props => props.$hasWarning ? props.theme.palette.messaging.critical.normalForeground : props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.7 : 1};
`

const CardOptionContent = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${rem(12)};
  align-items: center;
  min-width: 0;
`

const CardLogoFrame = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(34)};
  height: ${rem(24)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: ${rem(4)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const CardLogo = styled.img`
  max-width: ${rem(24)};
  max-height: ${rem(16)};
`

const CardDetails = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  min-width: 0;
`

const ExpiryWarningNotice = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${rem(4)};
  color: ${props => props.theme.palette.messaging.critical.normalForeground};
`

const DividerLabel = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: ${rem(12)};
  align-items: center;

  &::before,
  &::after {
    content: '';
    height: 1px;
    background: ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

type PaymentMethodOption =
  | { type: 'backup', id: string, cardId: string, card: App.StripePaymentCardV2 }
  | { type: 'saved', id: string, paymentMethodId: string, card: App.StripePaymentCardV2 }

interface Props {
  item: App.MemberSubscriptionItem
  mode: PickerMode
  onFailure?: () => void
  onSuccess?: () => void
  renewalDate?: string
}

function LuxPlusPaymentMethodPicker({ item, mode, onFailure, onSuccess, renewalDate }: Props) {
  const intl = useIntl()
  const stripeLocale = useStripeElementsLocale()
  const account = useAppSelector(state => state.auth.account)
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const currencyCode = useAppSelector(state => state.geo.currentCurrency)
  const [savedCards, setSavedCards] = useState<Array<App.StripePaymentCardV2>>([])
  const [offSessionCards, setOffSessionCards] = useState<Array<LuxPlusOffSessionPaymentCard>>([])
  const [primaryPaymentMethod, setPrimaryPaymentMethod] = useState<string>()
  const [selectedOptionId, setSelectedOptionId] = useState<string | undefined>()
  const [stripe, setStripe] = useState<Stripe | null>(null)
  const [newCardComplete, setNewCardComplete] = useState(false)
  const [newCardSubmitHandler, setNewCardSubmitHandler] = useState<(() => Promise<void>) | undefined>()
  const [preparedNewCardPaymentMethod, setPreparedNewCardPaymentMethod] = useState<LuxPlusCheckoutCardBackupCandidate | undefined>()
  const [isPreparingNewCardPaymentMethod, setIsPreparingNewCardPaymentMethod] = useState(false)
  const [hasLoadedPaymentMethods, setHasLoadedPaymentMethods] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const hasLoadedStripeRef = useRef(false)
  const isAddBackupMode = mode === 'add-backup'

  useEffect(() => {
    let mounted = true

    setHasLoadedPaymentMethods(false)

    Promise.allSettled([
      getSavedStripeCards(currencyCode),
      getStripeOffSessionCardsByRegionAndPurposeId(regionCode, item.id),
    ]).then(([savedCardsResult, offSessionCardsResult]) => {
      if (!mounted) return

      setSavedCards(savedCardsResult.status === 'fulfilled' ? savedCardsResult.value : [])

      if (offSessionCardsResult.status === 'fulfilled') {
        setOffSessionCards(offSessionCardsResult.value.cards)
        setPrimaryPaymentMethod(offSessionCardsResult.value.paymentMethod)
      } else {
        setOffSessionCards([])
        setPrimaryPaymentMethod(undefined)
      }

      setHasLoadedPaymentMethods(true)
    })

    return () => {
      mounted = false
    }
  }, [currencyCode, item.id, regionCode])

  useEffect(() => {
    if (hasLoadedStripeRef.current) return
    hasLoadedStripeRef.current = true

    getStripeInstance(currencyCode).then((result: StripeInstanceResult) => {
      setStripe(result.stripe ?? null)
    })
  }, [currencyCode])

  const activePaymentMethods = useMemo(() => {
    return offSessionCards.map(luxPlusOffSessionCardToLuxPlusCheckoutCardBackupCandidate)
  }, [offSessionCards])

  const backupCardOptions = useMemo<Array<PaymentMethodOption>>(() => {
    if (isAddBackupMode) return []

    return offSessionCards
      .filter(card => primaryPaymentMethod ? card.paymentMethod !== primaryPaymentMethod : card.priority !== 0)
      .sort((left, right) => (left.priority ?? Number.MAX_SAFE_INTEGER) - (right.priority ?? Number.MAX_SAFE_INTEGER))
      .map(card => ({
        type: 'backup',
        id: `backup:${card.id}`,
        cardId: card.id,
        card: card.card,
      }))
  }, [isAddBackupMode, offSessionCards, primaryPaymentMethod])

  const savedCardOptions = useMemo<Array<PaymentMethodOption>>(() => {
    return savedCards
      .filter(card => isAddBackupMode || card.paymentMethodId !== primaryPaymentMethod)
      .filter(card => !activePaymentMethods.some(activePaymentMethod => (
        hasMatchingLuxPlusCheckoutCardBackupCandidate(stripePaymentCardToLuxPlusCheckoutCardBackupCandidate(card), activePaymentMethod)
      )))
      .map(card => ({
        type: 'saved',
        id: `saved:${card.paymentMethodId}`,
        paymentMethodId: card.paymentMethodId,
        card,
      }))
  }, [activePaymentMethods, isAddBackupMode, primaryPaymentMethod, savedCards])

  const paymentMethodOptions = useMemo(() => [
    ...backupCardOptions,
    ...savedCardOptions,
  ], [backupCardOptions, savedCardOptions])

  const selectedOption = useMemo(() => (
    paymentMethodOptions.find(option => option.id === selectedOptionId && !getLuxPlusCardExpiresBeforeRenewal(option.card, renewalDate))
  ), [paymentMethodOptions, renewalDate, selectedOptionId])

  const matchingPreparedNewCardPaymentMethod = useMemo(() => {
    if (!preparedNewCardPaymentMethod) return undefined

    return activePaymentMethods.find(activePaymentMethod => (
      hasMatchingLuxPlusCheckoutCardBackupCandidate(preparedNewCardPaymentMethod, activePaymentMethod)
    ))
  }, [activePaymentMethods, preparedNewCardPaymentMethod])

  const hasPaymentMethodOptions = paymentMethodOptions.length > 0
  const selectedNewCard = selectedOptionId === 'new' || (hasLoadedPaymentMethods && !hasPaymentMethodOptions)
  const isPreparedNewCardAlreadySaved = isAddBackupMode && !!matchingPreparedNewCardPaymentMethod
  const shouldShowBackupCardAlreadySavedMessage = isAddBackupMode && selectedNewCard && newCardComplete && isPreparedNewCardAlreadySaved
  const canSubmit = selectedNewCard ?
    newCardComplete && !!newCardSubmitHandler && !isPreparingNewCardPaymentMethod && !isPreparedNewCardAlreadySaved :
    selectedOption?.type === 'backup' || (selectedOption?.type === 'saved' && !!stripe)

  const stripePaymentElementOptionsMode = useMemo<StripeElementsOptionsMode>(() => ({
    mode: 'setup',
    paymentMethodCreation: 'manual',
    currency: currencyCode.toLowerCase(),
    paymentMethodTypes: ['card'],
    appearance: STRIPE_ELEMENTS_APPEARANCE,
    locale: stripeLocale,
  }), [currencyCode, stripeLocale])

  const clearNewCardState = useCallback(() => {
    setNewCardComplete(false)
    setNewCardSubmitHandler(undefined)
    setPreparedNewCardPaymentMethod(undefined)
    setIsPreparingNewCardPaymentMethod(false)
  }, [])

  const handleOptionSelect = useCallback((optionId: string) => {
    const option = paymentMethodOptions.find(option => option.id === optionId)
    if (!option || getLuxPlusCardExpiresBeforeRenewal(option.card, renewalDate)) return

    clearNewCardState()
    setSelectedOptionId(optionId)
  }, [clearNewCardState, paymentMethodOptions, renewalDate])

  const handleNewCardSelect = useCallback(() => {
    clearNewCardState()
    setSelectedOptionId('new')
  }, [clearNewCardState])

  const handleSubmit = useCallback(async() => {
    try {
      setIsSubmitting(true)

      if (selectedNewCard) {
        await newCardSubmitHandler?.()
      } else if (selectedOption?.type === 'backup') {
        await promoteStripeOffSessionCardToPrimaryByRegionAndPurposeId(regionCode, item.id, selectedOption.cardId)
      } else if (selectedOption?.type === 'saved') {
        if (!stripe) throw new Error('Unable to load Stripe for LuxPlus payment method update')

        if (isAddBackupMode) {
          await appendLuxPlusBackupCard({
            currencyCode,
            paymentMethodId: selectedOption.paymentMethodId,
            purposeId: item.id,
            regionCode,
            stripe,
          })
        } else {
          await updateLuxPlusPrimaryCard({
            currencyCode,
            paymentMethodId: selectedOption.paymentMethodId,
            purposeId: item.id,
            regionCode,
            stripe,
          })
        }
      }

      setIsSubmitting(false)
      onSuccess?.()
    } catch {
      setIsSubmitting(false)
      onFailure?.()
    }
  }, [currencyCode, isAddBackupMode, item.id, newCardSubmitHandler, onFailure, onSuccess, regionCode, selectedNewCard, selectedOption, stripe])

  let newCardElement = null
  if (stripe && isAddBackupMode) {
    newCardElement = <LuxPlusAddBackupCardElement
      account={account}
      currencyCode={currencyCode}
      onCompleteChange={setNewCardComplete}
      onPreparedPaymentMethodChange={setPreparedNewCardPaymentMethod}
      onPreparingPaymentMethodChange={setIsPreparingNewCardPaymentMethod}
      onSubmitHandlerChange={handler => setNewCardSubmitHandler(() => handler)}
      purposeId={item.id}
      regionCode={regionCode}
      selected={selectedNewCard}
      stripe={stripe}
    />
  } else if (stripe) {
    newCardElement = <LuxPlusNewPrimaryCardElement
      currencyCode={currencyCode}
      onCompleteChange={setNewCardComplete}
      onSubmitHandlerChange={handler => setNewCardSubmitHandler(() => handler)}
      purposeId={item.id}
      regionCode={regionCode}
      selected={selectedNewCard}
      stripe={stripe}
    />
  }

  return <VerticalSpacer gap={16}>
    {!hasLoadedPaymentMethods && <TextLoadingBox width="100%" />}

    {hasPaymentMethodOptions && <VerticalSpacer gap={8}>
      <BodyText variant="large">
        {intl.formatMessage({
          id: 'mK0_mEyUFok8-sSPuz08',
          defaultMessage: 'Select a card',
          description: 'Section label above saved card options in the LuxPlus+ payment method update modal',
        })}
      </BodyText>
      {paymentMethodOptions.map(option => {
        const selected = selectedOptionId === option.id
        const expiresBeforeRenewal = getLuxPlusCardExpiresBeforeRenewal(option.card, renewalDate)
        return <CardOptionStack key={option.id}>
          <CardOption
            $disabled={expiresBeforeRenewal}
            $hasWarning={expiresBeforeRenewal}
            onClick={() => handleOptionSelect(option.id)}
          >
            <RadioButton
              checked={selected && !expiresBeforeRenewal}
              disabled={expiresBeforeRenewal}
              name={`lux-plus-payment-method-${mode}`}
              onChange={() => handleOptionSelect(option.id)}
              size="small"
            />
            <CardOptionContent>
              <CardLogoFrame>
                {option.card.logo && <CardLogo src={option.card.logo} alt="" />}
              </CardLogoFrame>
              <CardDetails>
                <BodyText variant="medium" wrap="truncate">
                  {intl.formatMessage({
                    id: 'USEMWCFHetSO4VVbzAyh',
                    defaultMessage: '{brand} ******** {last4}',
                    description: 'Backup payment card label in LuxPlus+ account management',
                  }, { brand: capitalise(option.card.brand), last4: option.card.last4 })}
                </BodyText>
                <BodyText variant="small" wrap="no-wrap">
                  {intl.formatMessage({
                    id: '8SMwGT0PKVvGkpmKUwaa',
                    defaultMessage: 'Exp. {expiry}',
                    description: 'Label displaying the expiration date of a saved payment card in the user account',
                  }, { expiry: option.card.expiry })}
                </BodyText>
              </CardDetails>
            </CardOptionContent>
          </CardOption>
          {expiresBeforeRenewal && <ExpiryWarningNotice>
            <LineInfoCircleIcon size="XS" colour="critical" />
            <BodyText variant="small" colour="critical">
              {intl.formatMessage({
                id: 'djB2qpqGAWG963JYhTw1',
                defaultMessage: 'Expires before your next renewal',
                description: 'Warning text when a LuxPlus+ backup payment method expires before renewal',
              })}
            </BodyText>
          </ExpiryWarningNotice>}
        </CardOptionStack>
      })}
    </VerticalSpacer>}

    {hasPaymentMethodOptions && <DividerLabel>
      <BodyText variant="small" colour="light-foreground-alt">
        {intl.formatMessage({
          id: 'sbRjUJPdI5P-ZKPIyYlg',
          defaultMessage: 'or',
          description: 'Divider label between saved card and new card options in backup payment method checkout panel',
        })}
      </BodyText>
    </DividerLabel>}

    {shouldShowBackupCardAlreadySavedMessage && <MessageBanner kind="warning" icon={<LineExclamationTriangleIcon />} density="compact">
      {intl.formatMessage({
        id: 'u-8qlnQc3CVhEHzCrsao',
        defaultMessage: 'This card is already saved for your {programName} membership, so it can\'t be added again as a backup payment method.',
        description: 'Warning shown when the entered backup card is already saved for LuxPlus membership payments',
      }, { programName: LUXURY_PLUS.PROGRAM_NAME })}
    </MessageBanner>}

    {hasLoadedPaymentMethods && <VerticalSpacer gap={8}>
      {hasPaymentMethodOptions && <CardOption onClick={handleNewCardSelect}>
        <RadioButton
          checked={selectedNewCard}
          name={`lux-plus-payment-method-${mode}`}
          onChange={handleNewCardSelect}
          size="small"
        />
        <BodyText variant="large">
          {intl.formatMessage({
            id: 'sBPZ7IdY0bpg0J5FBl7c',
            defaultMessage: 'Add a new card',
            description: 'Radio option label for adding a new LuxPlus+ backup card',
          })}
        </BodyText>
      </CardOption>}
      {selectedNewCard && stripe && <Elements stripe={stripe} options={stripePaymentElementOptionsMode}>
        {newCardElement}
      </Elements>}
      {selectedNewCard && !stripe && <TextLoadingBox width="100%" />}
    </VerticalSpacer>}

    <Group direction="horizontal" horizontalAlign="end">
      <TextButton kind="primary" size="large" disabled={!canSubmit || isSubmitting} onClick={handleSubmit}>
        {intl.formatMessage({
          id: 'FmR2BvFOt9kAHdr-LimK',
          defaultMessage: 'Update',
          description: 'Button text to update payment method during the checkout and payment process',
        })}
      </TextButton>
    </Group>
  </VerticalSpacer>
}

export default LuxPlusPaymentMethodPicker
